import { FC, useEffect } from "react";
import { observer } from "mobx-react";
import {
  acquiringCompanyName,
  capitalChangesTitle,
  counterpartyOf,
  getParentCompanyStatus,
  financialInstrumentsQuestion,
  getShareholderNoticeDeadlineWarning,
  managementTakeoverQuestion,
  MergerForm,
  noticeWaiverQuestion,
  SubsidiaryMergerCapitalChangeModel,
  SubsidiaryMergerCompanyRole,
  transferringCompanyName,
} from "./mergerDomain";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { Alert } from "@/components-v2/Alert";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { SuggestedCompany } from "@/types/CompanyInfo";
import { TMessage } from "@/i18n/tt";
import { TextInput } from "@/components-v2/TextInput";
import { MSelect } from "@/components-v2/Select";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { IconButton, LinkButton } from "@/components-v2/Button";
import { RadioFieldQuestion } from "@/common/commonComponents";
import { DateQuestion } from "@/dbot/common/commonComponents";
import { formatOrgNum } from "@/utils/other";
import { when } from "@/utils/functions";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";

import { useTranslation } from "@/stores/RootStoreContext";

const parentWarningKeys = {
  "no-own-parent": "decisions.merger.parent.warning.no-own-parent",
  unconfirmed: "decisions.merger.parent.warning.unconfirmed",
  "different-parents": "decisions.merger.parent.warning.different-parents",
  "subsidiary-not-wholly-owned": "decisions.merger.parent.warning.subsidiary-not-wholly-owned",
  "parent-unconfirmed": "decisions.merger.parent.warning.parent-unconfirmed",
  "not-the-parent": "decisions.merger.parent.warning.not-the-parent",
} as const;

const ManagedInCounterpartAlert = observer(function ManagedInCounterpartAlert({
  form,
}: {
  form: MergerForm;
}) {
  const { t } = useTranslation();
  const managingCompany = counterpartyOf(form.data);
  if (!form.isManagedInCounterpartProject || managingCompany === undefined) {
    return null;
  }
  return (
    <Alert
      type={"info"}
      message={
        <VBox gap={"sm"}>
          <span>
            {t("decisions.merger.managed-in-counterpart", { companyName: managingCompany.name })}
          </span>
          <div>
            <LinkButton
              label={t("dbot.summary.mergerCounterpart.open-project")}
              onClick={form.openCounterpartProject}
            />
          </div>
        </VBox>
      }
    />
  );
});

const ParentCompanyStatusAlert = observer(function ParentCompanyStatusAlert({
  form,
}: {
  form: MergerForm;
}) {
  const status = getParentCompanyStatus(form);
  if (status === undefined) {
    return null;
  }
  if (status.kind === "common-parent") {
    return (
      <Alert
        type={"info"}
        message={{
          key: "decisions.merger.parent.common",
          params: {
            name: status.parent.name,
            orgNo: { type: "org-no", value: status.parent.orgNo },
          },
        }}
      />
    );
  }
  if (status.kind === "parent-owns-subsidiary") {
    return (
      <Alert
        type={"info"}
        message={{
          key: "decisions.merger.parent.wholly-owned",
          params: {
            parentName: status.parent.name,
            subsidiaryName: status.subsidiary.name,
          },
        }}
      />
    );
  }
  return <Alert type={"warning"} message={{ key: parentWarningKeys[status.kind] }} />;
});

const CapitalChangeRow = observer(function CapitalChangeRow({
  form,
  change,
  showLabels,
}: {
  form: MergerForm;
  change: SubsidiaryMergerCapitalChangeModel;
  showLabels: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box gap={"sm"} alignItems={"center"}>
      <Grid cols={2} grow>
        <GridItem xs={2} md={1}>
          <MSelect
            fullWidth
            label={showLabels ? transferringCompanyName(form) : undefined}
            model={change.transferringCompanyShareTypeCode}
            disabled={form.isReadOnly}
            name={`capitalChangeTransferringShareClass-${change.id}`}
          />
        </GridItem>
        <GridItem xs={2} md={1}>
          <MSelect
            fullWidth
            label={showLabels ? acquiringCompanyName(form) : undefined}
            model={change.acquiringCompanyShareTypeCode}
            disabled={form.isReadOnly}
            name={`capitalChangeAcquiringShareClass-${change.id}`}
          />
        </GridItem>
      </Grid>
      <Box marginTop={showLabels ? "lg" : false}>
        <IconButton
          icon={"x"}
          tooltip={t("remove")}
          onClick={() => form.removeCapitalChange(change.id)}
          disabled={form.isReadOnly}
        />
      </Box>
    </Box>
  );
});

export const MergerView: FC<{
  form: MergerForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();

  useEffect(() => form.init(), [form]);

  return (
    <VBox gap={"md"}>
      <ManagedInCounterpartAlert form={form} />
      <VBox>
        <div>{t("decisions.merger.merger-type.question")}</div>
        <MSelect
          fullWidth
          model={form.mergerType}
          name="mergerDecisionType"
          placeholder={t("choose")}
          disabled={form.isReadOnly}
        />
      </VBox>

      {form.mergerType.value !== undefined && (
        <>
          <VBox>
            <div>{t("decisions.merger.role-question", { companyName: form.ownCompany.name })}</div>
            <MRadioButtonGroup
              model={form.companyRole}
              name="mergerDecisionRole"
              disabled={form.isReadOnly}
              onChange={(_, option) => form.setCompanyRole(option)}
            />
          </VBox>
          {form.companyRole.value !== undefined && (
            <>
              <VBox>
                {form.companyRole.value === SubsidiaryMergerCompanyRole.ACQUIRING ? (
                  <>
                    <div>{t("decisions.merger.role.TRANSFERRING")}</div>
                    <div>{t("decisions.merger.dissolves-after-merger")}</div>
                  </>
                ) : (
                  <>
                    <div>
                      {t("decisions.merger.company-dissolves-after-merger", {
                        companyName: form.ownCompany.name,
                      })}
                    </div>
                    <div>{t("decisions.merger.role.ACQUIRING")}</div>
                  </>
                )}
                <CompanySearch
                  name="mergerDecisionCompanySearch"
                  orgNoName="mergerDecisionOrgNo"
                  isLocked={form.isReadOnly}
                  suggestedCompanies={form.suggestedCompanies}
                  selectedCompany={form.selectedCompany}
                  inputValue={form.searchCompanyModel.inputValue}
                  onInputValueChange={form.setCompanySearch}
                  onSelect={form.selectCompany}
                  onClear={form.clearSelectedCompany}
                  error={form.companyError}
                />
                <ParentCompanyStatusAlert form={form} />
              </VBox>

              <DateQuestion
                question={t("decisions.merger.merger-date-question")}
                field={form.mergerDate}
                isLocked={form.isReadOnly}
              />
              <DateQuestion
                question={t("decisions.merger.accounting-effective-date-question")}
                infoText={t("decisions.merger.accounting-effective-date-guidance")}
                field={form.accountingEffectiveDate}
                isLocked={form.isReadOnly}
              />

              <RadioFieldQuestion
                row
                question={managementTakeoverQuestion(form)}
                infoText={{ key: "decisions.merger.management-takeover.guidance" }}
                field={form.managementTakeover}
                fieldName={"mergerManagementTakeover"}
                isLocked={form.isReadOnly}
              />
              <RadioFieldQuestion
                row
                question={financialInstrumentsQuestion(form)}
                field={form.hasIssuedFinancialInstruments}
                fieldName={"mergerFinancialInstruments"}
                isLocked={form.isReadOnly}
              />
              {form.hasIssuedFinancialInstruments.value && (
                <LocalizedTextEditor
                  model={form.financialInstrumentsDescription}
                  inputType={"string"}
                />
              )}
              <RadioFieldQuestion
                row
                question={{ key: "decisions.merger.special-rights.question" }}
                field={form.hasSpecialRights}
                fieldName={"mergerSpecialRights"}
                isLocked={form.isReadOnly}
              />
              {form.hasSpecialRights.value && (
                <LocalizedTextEditor model={form.specialRightsDescription} inputType={"string"} />
              )}
              {form.isParentSubsidiaryMerger && (
                <>
                  <RadioFieldQuestion
                    row
                    question={noticeWaiverQuestion(form)}
                    infoText={{ key: "decisions.merger.notice-waiver.guidance" }}
                    field={form.noticeDeadlineWaived}
                    fieldName={"mergerNoticeDeadlineWaived"}
                    isLocked={form.isReadOnly}
                  />
                  {when(getShareholderNoticeDeadlineWarning(form), (earliestDate) => (
                    <Alert
                      type={"info"}
                      message={{
                        key: "decisions.merger.notice-deadline.warning",
                        params: { date: { type: "date", value: earliestDate } },
                      }}
                    />
                  ))}
                  <RadioFieldQuestion
                    question={{ key: "decisions.merger.annual-accounts-location.question" }}
                    field={form.annualAccountsLocation}
                    fieldName={"mergerAnnualAccountsLocation"}
                    isLocked={form.isReadOnly}
                  />
                </>
              )}
              {!form.isParentSubsidiaryMerger && (
                <VBox>
                  <div>{t(capitalChangesTitle(form))}</div>
                  {form.capitalChanges.map((change, index) => (
                    <CapitalChangeRow
                      key={change.id}
                      form={form}
                      change={change}
                      showLabels={index === 0}
                    />
                  ))}
                  <div>
                    <LinkButton
                      label={t("decisions.merger.capital-changes.add")}
                      icon={"plus"}
                      disabled={form.isReadOnly}
                      onClick={form.addCapitalChange}
                    />
                  </div>
                </VBox>
              )}
              {form.selectedCompany && (
                <VBox>
                  <RadioFieldQuestion
                    question={{ key: "decisions.merger.invoice-recipient.question" }}
                    infoText={{ key: "decisions.merger.invoice-recipient.info" }}
                    field={form.invoiceRecipient}
                    fieldName={"mergerInvoiceRecipient"}
                    isLocked={form.isReadOnly}
                  />
                  {form.isOtherInvoiceRecipient && (
                    <CompanySearch
                      name="mergerInvoiceRecipientCompanySearch"
                      orgNoName="mergerInvoiceRecipientOrgNo"
                      isLocked={form.isReadOnly}
                      suggestedCompanies={form.suggestedInvoiceCompanies}
                      selectedCompany={form.selectedInvoiceCompany}
                      inputValue={form.invoiceCompanySearchModel.inputValue}
                      onInputValueChange={form.setInvoiceCompanySearch}
                      onSelect={form.selectInvoiceCompany}
                      onClear={form.clearInvoiceCompany}
                      error={form.invoiceCompanySearchModel.error}
                    />
                  )}
                </VBox>
              )}
            </>
          )}
        </>
      )}
    </VBox>
  );
});

const CompanySearch = observer(function CompanySearch({
  name,
  orgNoName,
  isLocked,
  suggestedCompanies,
  selectedCompany,
  inputValue,
  onInputValueChange,
  onSelect,
  onClear,
  error,
}: {
  name: string;
  orgNoName: string;
  isLocked: boolean;
  suggestedCompanies: SuggestedCompany[];
  selectedCompany?: SuggestedCompany;
  inputValue: string;
  onInputValueChange: (input: string) => void;
  onSelect: (company: SuggestedCompany) => void;
  onClear: () => void;
  error?: TMessage;
}) {
  const { t } = useTranslation();
  return (
    <Grid cols={2}>
      <GridItem xs={2} md={1}>
        <Autocomplete
          fullWidth
          hideCaret
          minQueryLength={2}
          name={name}
          disabled={isLocked}
          items={suggestedCompanies}
          value={selectedCompany}
          idFn={(company) => company.organizationnumber}
          labelFn={(company) => company.name}
          inputValue={inputValue}
          onInputValueChange={onInputValueChange}
          onSelect={onSelect}
          onClear={onClear}
          error={Boolean(error)}
          message={when(error, t)}
        />
      </GridItem>
      <GridItem xs={2} md={1}>
        <TextInput
          disabled
          placeholder={t("decisions.merger.organization-number")}
          value={formatOrgNum(selectedCompany?.organizationnumber)}
          name={orgNoName}
        />
      </GridItem>
    </Grid>
  );
});
